// src/components/permissions/ScopeSelector.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Chip,
  alpha,
  Stack,
  Paper,
} from '@mui/material';
import {
  InfoOutlined,
  AllInclusive as AllIcon,
  PersonOutlined as OwnIcon,
  GroupOutlined as TeamIcon,
  BusinessOutlined as OrganizationIcon,
  AssignmentOutlined as AssignedIcon,
  FolderOutlined as ProjectIcon,
  BrandingWatermarkOutlined as BrandIcon,
  PersonPinOutlined as ClientIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const ScopePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  marginBottom: theme.spacing(3),
  boxShadow: 'none',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
}));

const StyledChip = styled(Chip)(({ theme, selected }) => ({
  margin: theme.spacing(0.5),
  transition: 'all 0.2s ease',
  fontWeight: 500,
  ...(selected && {
    boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.25)}`,
    background: theme.palette.mode === 'dark' 
      ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
      : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    color: theme.palette.primary.contrastText,
  }),
}));

// Icons for each scope
const SCOPE_ICONS = {
  own: <OwnIcon fontSize="small" />,
  assigned: <AssignedIcon fontSize="small" />,
  team: <TeamIcon fontSize="small" />,
  project: <ProjectIcon fontSize="small" />,
  brand: <BrandIcon fontSize="small" />,
  client: <ClientIcon fontSize="small" />,
  organization: <OrganizationIcon fontSize="small" />,
  all: <AllIcon fontSize="small" />,
};

// Descriptions for each scope
const SCOPE_DESCRIPTIONS = {
  own: "Access only to resources created by the user",
  assigned: "Access only to resources assigned to the user",
  team: "Access to resources within the user's team",
  project: "Access to resources within specific projects",
  brand: "Access to resources belonging to specific brands",
  client: "Access to resources belonging to specific clients",
  organization: "Access to resources within the organization",
  all: "Access to all resources regardless of ownership",
};

/**
 * Component for selecting permission scope
 * 
 * @param {Object} props
 * @param {Object|Array} props.scopes - Available scopes
 * @param {string} props.selectedScope - Currently selected scope
 * @param {Function} props.onChange - Callback when scope changes
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 */
const ScopeSelector = ({
  scopes = {},
  selectedScope = 'all',
  onChange,
  disabled = false,
}) => {
  // Convert scopes object to array if needed
  const scopesArray = useMemo(() => {
    if (!scopes || (typeof scopes === 'object' && Object.keys(scopes).length === 0)) {
      // Default scopes if none provided
      return ['own', 'assigned', 'team', 'organization', 'all'];
    }
    return Array.isArray(scopes) 
      ? scopes 
      : Object.values(scopes);
  }, [scopes]);
  
  // Get the keys for each scope value for tooltips
  const scopeKeys = useMemo(() => {
    if (Array.isArray(scopes)) {
      return scopesArray.reduce((acc, scope) => {
        acc[scope] = scope.toUpperCase();
        return acc;
      }, {});
    }
    
    return Object.entries(scopes).reduce((acc, [key, value]) => {
      acc[value] = key;
      return acc;
    }, {});
  }, [scopes, scopesArray]);
  
  // Handle scope change
  const handleScopeChange = (scope) => {
    if (disabled) return;
    onChange(scope);
  };
  
  return (
    <ScopePaper>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Permission Scope
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Set how broadly these permissions apply
        </Typography>
      </Box>
      
      <Stack 
        direction="row" 
        flexWrap="wrap"
        sx={{ 
          justifyContent: { xs: 'center', sm: 'flex-start' },
          m: -0.5,
        }}
      >
        {scopesArray.map((scope) => (
          <Tooltip
            key={scope}
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {scopeKeys[scope]}
                </Typography>
                <Typography variant="caption">
                  {SCOPE_DESCRIPTIONS[scope] || `Scope: ${scope}`}
                </Typography>
              </Box>
            }
            arrow
          >
            <StyledChip
              icon={SCOPE_ICONS[scope] || <InfoOutlined fontSize="small" />}
              label={scope}
              onClick={() => handleScopeChange(scope)}
              clickable={!disabled}
              selected={selectedScope === scope}
              variant={selectedScope === scope ? "filled" : "outlined"}
              color={selectedScope === scope ? "primary" : "default"}
              disabled={disabled}
            />
          </Tooltip>
        ))}
      </Stack>
    </ScopePaper>
  );
};

export default ScopeSelector;